import React, {useCallback, useEffect, useState} from 'react';
import {
  View,
  FlatList,
  Image,
  StyleSheet,
  TouchableOpacity,
  RefreshControl,
  Alert,
  Dimensions,
} from 'react-native';
import {
  Text,
  Appbar,
  ActivityIndicator,
  Snackbar,
} from 'react-native-paper';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute} from '@react-navigation/native';
import {launchImageLibrary} from 'react-native-image-picker';
import {useGalleryStore} from '../../store/useGalleryStore';
import {colors, spacing, borderRadius} from '../../theme';

const SCREEN_WIDTH = Dimensions.get('window').width;
const IMAGE_GAP = spacing.xs;
const IMAGE_SIZE = (SCREEN_WIDTH - spacing.md * 2 - IMAGE_GAP) / 2;

const GalleryFolderImagesScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const {folderId, folderName} = route.params;

  const {images, isLoading, pagination, fetchImages, uploadImages, deleteImage, toggleFavorite, clearImages} =
    useGalleryStore();

  const [refreshing, setRefreshing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  useEffect(() => {
    fetchImages(folderId, 0);
    return () => clearImages();
  }, [folderId, fetchImages, clearImages]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchImages(folderId, 0);
    setRefreshing(false);
  }, [folderId, fetchImages]);

  const handleLoadMore = useCallback(() => {
    if (!isLoading && !pagination.last) {
      fetchImages(folderId, pagination.page + 1);
    }
  }, [folderId, isLoading, pagination, fetchImages]);

  const handleUpload = useCallback(async () => {
    try {
      const result = await launchImageLibrary({
        mediaType: 'photo',
        selectionLimit: 10,
        quality: 0.8,
      });

      if (result.didCancel || !result.assets?.length) return;

      setUploading(true);
      await uploadImages(folderId, result.assets);
      setSnackbar(t('gallery.imagesUploaded'));
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    } finally {
      setUploading(false);
    }
  }, [folderId, uploadImages, t]);

  const handleDeleteImage = useCallback(
    image => {
      if (image.isDefault) {
        setSnackbar(t('gallery.cannotDeleteDefaultImage'));
        return;
      }
      Alert.alert(t('common.delete'), t('gallery.deleteImage'), [
        {text: t('common.cancel'), style: 'cancel'},
        {
          text: t('common.delete'),
          style: 'destructive',
          onPress: async () => {
            try {
              await deleteImage(image.id);
            } catch (e) {
              setSnackbar(e.response?.data?.message || t('common.error'));
            }
          },
        },
      ]);
    },
    [deleteImage, t],
  );

  const handleToggleFavorite = useCallback(
    async image => {
      try {
        await toggleFavorite(image.id, !image.isFavorite);
      } catch (e) {
        setSnackbar(e.response?.data?.message || t('common.error'));
      }
    },
    [toggleFavorite, t],
  );

  const renderImage = useCallback(
    ({item}) => (
      <TouchableOpacity
        style={styles.imageCard}
        onLongPress={() => handleDeleteImage(item)}
        activeOpacity={0.8}>
        <Image
          style={styles.image}
          source={{uri: item.imageUrl}}
          resizeMode="cover"
        />
        <TouchableOpacity
          style={styles.favoriteButton}
          onPress={() => handleToggleFavorite(item)}
          hitSlop={{top: 8, bottom: 8, left: 8, right: 8}}>
          <MaterialCommunityIcons
            name={item.isFavorite ? 'heart' : 'heart-outline'}
            size={22}
            color={item.isFavorite ? colors.error : colors.white}
          />
        </TouchableOpacity>
      </TouchableOpacity>
    ),
    [handleDeleteImage, handleToggleFavorite],
  );

  const keyExtractor = useCallback(item => String(item.id), []);

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction
          onPress={() => navigation.goBack()}
          color={colors.white}
        />
        <Appbar.Content
          title={folderName}
          titleStyle={styles.appbarTitle}
        />
        {uploading ? (
          <ActivityIndicator
            size={24}
            color={colors.white}
            style={styles.uploadingIndicator}
          />
        ) : (
          <Appbar.Action
            icon="plus"
            color={colors.white}
            onPress={handleUpload}
          />
        )}
      </Appbar.Header>

      {isLoading && images.length === 0 ? (
        <View style={styles.centered}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : (
        <FlatList
          data={images}
          keyExtractor={keyExtractor}
          renderItem={renderImage}
          numColumns={2}
          columnWrapperStyle={styles.row}
          contentContainerStyle={styles.listContent}
          onEndReached={handleLoadMore}
          onEndReachedThreshold={0.3}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={handleRefresh}
              colors={[colors.primary]}
            />
          }
          ListEmptyComponent={
            <View style={styles.centered}>
              <MaterialCommunityIcons
                name="image-off-outline"
                size={64}
                color={colors.textLight}
              />
              <Text style={styles.emptyText}>{t('gallery.noImages')}</Text>
              <Text style={styles.emptySubtext}>{t('gallery.tapToAdd')}</Text>
            </View>
          }
          ListFooterComponent={
            isLoading && images.length > 0 ? (
              <ActivityIndicator
                size="small"
                color={colors.primary}
                style={styles.footer}
              />
            ) : null
          }
        />
      )}

      <Snackbar
        visible={!!snackbar}
        onDismiss={() => setSnackbar('')}
        duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  uploadingIndicator: {marginRight: spacing.md},
  centered: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  listContent: {padding: spacing.md, flexGrow: 1},
  row: {justifyContent: 'space-between', marginBottom: IMAGE_GAP},
  imageCard: {
    width: IMAGE_SIZE,
    height: IMAGE_SIZE,
    borderRadius: borderRadius.md,
    overflow: 'hidden',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  favoriteButton: {
    position: 'absolute',
    top: spacing.xs,
    right: spacing.xs,
    backgroundColor: 'rgba(0,0,0,0.3)',
    borderRadius: borderRadius.round,
    padding: 4,
  },
  emptyText: {
    fontSize: 16,
    color: colors.textSecondary,
    marginTop: spacing.md,
  },
  emptySubtext: {
    fontSize: 14,
    color: colors.textLight,
    marginTop: spacing.xs,
  },
  footer: {
    paddingVertical: spacing.md,
  },
});

export default GalleryFolderImagesScreen;
